import React, { useEffect, useState, useRef } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { Send, Bot, User, MessageSquare, RefreshCw, Paperclip, X, Users, UserPlus, Search } from 'lucide-react';
import io from 'socket.io-client';
import { BASE_URL } from '../config';


const normalizeJid = (jid) => {
    if (!jid || typeof jid !== 'string') return '';
    const [main, suffix] = jid.split('@');
    const domain = suffix ? `@${suffix}` : '@c.us';

    if (main.startsWith('55') && (!suffix || suffix === 'c.us')) {
        const phone = main.slice(2);
        if (phone.length === 11 && phone[2] === '9') {
            const ddd = phone.slice(0, 2);
            const number = phone.slice(3);
            return `55${ddd}${number}${domain}`;
        }
    }
    return jid;
};

const isSameJid = (jid1, jid2) => {
    return normalizeJid(jid1) === normalizeJid(jid2);
};

// Componente para carregar e exibir mídias sob demanda
const MediaContent = ({ chatId, messageId, type, isMe }) => {
    const [mediaData, setMediaData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let isMounted = true;
        api.get(`/contatos/chats/${chatId}/messages/${messageId}/media`)
            .then(res => {
                if (isMounted) {
                    setMediaData(res.data);
                    setLoading(false);
                }
            })
            .catch(err => {
                console.error("Erro ao carregar mídia:", err);
                if (isMounted) {
                    setError(true);
                    setLoading(false);
                }
            });
        return () => { isMounted = false; };
    }, [chatId, messageId]);

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px', fontSize: '11px', color: isMe ? 'rgba(255,255,255,0.8)' : '#64748b' }}>
                <RefreshCw size={12} className="animate-spin" /> Carregando mídia...
            </div>
        );
    }

    if (error || !mediaData) {
        return (
            <div style={{ fontSize: '11px', color: isMe ? '#fee2e2' : '#ef4444', fontStyle: 'italic', padding: '4px' }}>
                Mídia não disponível
            </div>
        );
    }

    const { mimetype, data, filename } = mediaData;
    const srcUrl = `data:${mimetype};base64,${data}`;

    // Imagens, fotos e figurinhas (stickers)
    if (mimetype.startsWith('image/') || type === 'sticker') {
        const isSticker = type === 'sticker' || mimetype.includes('webp');
        return (
            <div style={{
                marginTop: '5px',
                borderRadius: '8px',
                overflow: 'hidden',
                maxWidth: isSticker ? '120px' : '240px',
                border: isMe ? '1px solid rgba(255,255,255,0.2)' : '1px solid #e2e8f0',
                backgroundColor: isSticker ? 'transparent' : '#fff',
                padding: isSticker ? '0' : '4px'
            }}>
                <img src={srcUrl} alt={filename || "Foto"} style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '6px' }} />
            </div>
        );
    }

    // Vídeos
    if (mimetype.startsWith('video/')) {
        return (
            <div style={{ marginTop: '5px', borderRadius: '8px', overflow: 'hidden', maxWidth: '280px' }}>
                <video src={srcUrl} controls style={{ width: '100%', display: 'block', borderRadius: '8px' }} />
            </div>
        );
    }

    // Áudio e Mensagens de Voz
    if (mimetype.startsWith('audio/')) {
        return (
            <div style={{ marginTop: '5px', maxWidth: '240px' }}>
                <audio src={srcUrl} controls style={{ width: '100%', maxHeight: '35px' }} />
            </div>
        );
    }

    // Outros tipos de arquivos / Documentos
    return (
        <a
            href={srcUrl}
            download={filename || `documento_${messageId}`}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '5px',
                padding: '6px 12px',
                backgroundColor: isMe ? 'rgba(255,255,255,0.2)' : '#f1f5f9',
                borderRadius: '6px',
                color: isMe ? '#fff' : '#2563eb',
                textDecoration: 'none',
                fontSize: '11px',
                fontWeight: 'bold',
                border: isMe ? 'none' : '1px solid #e2e8f0'
            }}
        >
            <Paperclip size={12} /> {filename || 'Baixar Arquivo'}
        </a>
    );
};

const ChatPage = () => {
    const [activeInstance, setActiveInstance] = useState('B'); // B é o padrão (Suporte)
    const [chats, setChats] = useState([]);
    const [activeChat, setActiveChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loadingChats, setLoadingChats] = useState(false);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [sending, setSending] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);

    // Novos estados de contatos integrados
    const [sidebarTab, setSidebarTab] = useState('chats'); // 'chats' ou 'contacts'
    const [contacts, setContacts] = useState([]);
    const [groups, setGroups] = useState([]); // Lista de grupos
    const [loadingContacts, setLoadingContacts] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [newContactName, setNewContactName] = useState('');
    const [newContactPhone, setNewContactPhone] = useState('');
    const [savingContact, setSavingContact] = useState(false);
    const [socketConnected, setSocketConnected] = useState(false);

    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);

    const activeChatRef = useRef(activeChat);
    const activeInstanceRef = useRef(activeInstance);

    useEffect(() => {
        activeChatRef.current = activeChat;
    }, [activeChat]);

    useEffect(() => {
        activeInstanceRef.current = activeInstance;
    }, [activeInstance]);
    // Fetch active chats
    const fetchChats = async (silent = false, instance = activeInstance) => {
        if (!silent) setLoadingChats(true);
        try {
            console.log(`🔄 fetchChats chamado. Silent: ${silent}, Instance: ${instance}`);
            const res = await api.get(`/contatos/chats?instance=${instance}`);
            
            // Garante que a conversa ativa atual sempre apareça com 0 não lidas no frontend
            const currentChat = activeChatRef.current;
            const updatedChats = res.data.map(c => 
                currentChat && isSameJid(c.whatsappId, currentChat.whatsappId) 
                    ? { ...c, unreadCount: 0 } 
                    : c
            );
            setChats(updatedChats);
            console.log(`✅ fetchChats: ${res.data.length} chats recebidos.`);

            const pendingChatId = sessionStorage.getItem('open_chat_id');
            const pendingChatName = sessionStorage.getItem('open_chat_name');
            if (pendingChatId && !silent) {
                // Pequeno delay contra dupla montagem de Strict Mode em desenvolvimento
                setTimeout(() => {
                    sessionStorage.removeItem('open_chat_id');
                    sessionStorage.removeItem('open_chat_name');
                }, 1000);
                const foundChat = res.data.find(c => isSameJid(c.whatsappId, pendingChatId));
                if (foundChat) {
                    setActiveChat(foundChat);
                    fetchMessages(pendingChatId, instance);
                } else {
                    const mockChat = {
                        whatsappId: pendingChatId,
                        name: pendingChatName || pendingChatId.split('@')[0],
                        aiEnabled: true
                    };
                    setChats(prev => {
                        if (prev.find(c => isSameJid(c.whatsappId, pendingChatId))) return prev;
                        return [mockChat, ...prev];
                    });
                    setActiveChat(mockChat);
                    fetchMessages(pendingChatId, instance);
                }
            }
        } catch (err) {
            console.error('Error fetching chats:', err);
            toast.error('Erro ao buscar conversas');
        } finally {
            if (!silent) setLoadingChats(false);
        }
    };

    // Fetch all contacts from db
    const fetchContacts = async () => {
        setLoadingContacts(true);
        try {
            const res = await api.get('/contatos');
            setContacts(res.data);
        } catch (err) {
            console.error('Error fetching contacts:', err);
            toast.error('Erro ao buscar lista de contatos');
        } finally {
            setLoadingContacts(false);
        }
    };

    // Fetch all groups from db
    const fetchGroups = async () => {
        try {
            const res = await api.get('/grupos');
            setGroups(res.data);
        } catch (err) {
            console.error('Error fetching groups:', err);
        }
    };

    // Fetch messages
    const fetchMessages = async (whatsappId, instance = activeInstance, silent = false) => {
        if (!silent) setLoadingMessages(true);
        try {
            const res = await api.get(`/contatos/chats/${whatsappId}/messages?instance=${instance}`);
            // Ordena mensagens cronologicamente (do mais antigo para o mais novo) antes de renderizar
            const sortedMessages = [...res.data].sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
            setMessages(sortedMessages);
            if (!silent) scrollToBottom(); // Scroll to bottom on initial load
            else setTimeout(scrollToBottom, 100); // Small delay for real-time messages
        } catch (err) {
            console.error('Error fetching messages:', err);
            if (!silent) toast.error('Erro ao buscar mensagens');
        } finally {
            if (!silent) setLoadingMessages(false);
        }
    };

    // Toggle AI for specific contact
    const toggleAI = async (chatId, currentStatus) => {
        const newStatus = !currentStatus;
        try {
            await api.patch(`/contatos/toggle-ai/${chatId}`, { aiEnabled: newStatus });
            toast.success(newStatus ? 'IA Reativada para este chat' : 'IA Desativada! Atendimento humanizado ativo.');

            // Update local state for chats
            setChats(prev => prev.map(c => isSameJid(c.whatsappId, chatId) ? { ...c, aiEnabled: newStatus } : c));
            if (activeChat && isSameJid(activeChat.whatsappId, chatId)) {
                setActiveChat(prev => ({ ...prev, aiEnabled: newStatus }));
            }
        } catch (err) {
            console.error('Error toggling AI:', err);
            toast.error('Erro ao alternar status da IA');
        }
    };

    // Handle File Attachment Selection
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 15 * 1024 * 1024) {
            return toast.error("O arquivo selecionado é muito grande (máximo 15MB).");
        }

        const reader = new FileReader();
        reader.onload = () => {
            setSelectedFile({
                name: file.name,
                mimetype: file.type,
                base64: reader.result.split(',')[1]
            });
        };
        reader.readAsDataURL(file);
    };

    // Send manual message
    const handleSendMessage = async (e) => {
        e.preventDefault();
        if ((!newMessage.trim() && !selectedFile) || !activeChat) return;

        setSending(true);
        const textToSend = newMessage;
        const fileToSend = selectedFile;
        setNewMessage('');
        setSelectedFile(null);

        try {
            const res = await api.post(`/contatos/chats/${activeChat.whatsappId}/send`, {
                message: textToSend,
                file: fileToSend,
                instance: activeInstance
            });

            if (res.data && res.data.message) {
                setMessages(prev => {
                    const updated = [...prev, res.data.message];
                    return updated.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
                });
            }

            scrollToBottom();
        } catch (err) {
            console.error('Error sending message:', err);
            toast.error('Erro ao enviar mensagem');
            setNewMessage(textToSend); // Restore text if failed
            setSelectedFile(fileToSend); // Restore file if failed
        } finally {
            setSending(false);
        }
    };

    const scrollToBottom = () => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    // Formata o telefone digitado para o JID do WhatsApp
    const formatWhatsAppId = (phone) => {
        let clean = phone.replace(/\D/g, ''); // Apenas números
        if (!clean.endsWith('@c.us') && !clean.endsWith('@g.us')) {
            clean += '@c.us';
        }
        return clean;
    };

    // Adiciona contato novo na base e já abre a conversa
    const handleAddContact = async (e) => {
        e.preventDefault();
        if (!newContactName.trim() || !newContactPhone.trim()) return;

        setSavingContact(true);
        const formattedJid = formatWhatsAppId(newContactPhone);
        try {
            await api.post('/contatos', {
                nome: newContactName,
                whatsappId: formattedJid
            });
            toast.success("Contato adicionado com sucesso!");

            setNewContactName('');
            setNewContactPhone('');
            setShowAddModal(false);

            // Recarrega contatos
            await fetchContacts();
            await fetchChats(true);

            // Inicia a conversa automaticamente
            handleSelectContactToChat({ nome: newContactName, whatsappId: formattedJid });
        } catch (err) {
            console.error('Error adding contact:', err);
            toast.error(err.response?.data?.error || 'Erro ao adicionar contato');
        } finally {
            setSavingContact(false);
        }
    };

    // Seleciona um contato da aba contatos e abre a conversa
    const handleSelectContactToChat = (contact) => {
        const existingChat = chats.find(c => isSameJid(c.whatsappId, contact.whatsappId));
        if (existingChat) {
            selectChat(existingChat);
        } else {
            const mockChat = {
                whatsappId: contact.whatsappId,
                name: contact.nome || contact.whatsappId.split('@')[0],
                aiEnabled: true
            };
            setChats(prev => {
                if (prev.find(c => isSameJid(c.whatsappId, contact.whatsappId))) return prev;
                return [mockChat, ...prev];
            });
            selectChat(mockChat);
        }
        setSidebarTab('chats');
        setSearchQuery('');
    };

    // Load initial chats & contacts
    useEffect(() => {
        fetchChats();
        fetchContacts();
        fetchGroups();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Listen to real-time messages via socket
    // Listen to real-time messages via socket
    useEffect(() => {
        console.log(`🔌 Inicializando socket em ChatPage com URL: ${BASE_URL}`);
        const socketInstance = io(BASE_URL, {
            transports: ['websocket', 'polling'],
            reconnectionAttempts: 10,
            reconnectionDelay: 2000
        });

        const handleIncomingMessage = (msg) => {
            console.log('📥 Recebido whatsapp_message:', msg);
            const currentInstance = activeInstanceRef.current;
            const currentChat = activeChatRef.current;

            console.log(`🔍 Instância Atual: ${currentInstance}, Mensagem para: ${msg.clientId}`);

            // Ignora se a mensagem não for para a aba que o admin está olhando
            if (msg.clientId && msg.clientId !== currentInstance) {
                console.log('🚫 Mensagem ignorada por ser de outra instância.');
                return;
            }

            if (currentChat && (isSameJid(msg.from, currentChat.whatsappId) || isSameJid(msg.to, currentChat.whatsappId))) {
                // Em vez de adicionar localmente (o que quebra mídias), buscamos a lista atualizada do backend
                fetchMessages(currentChat.whatsappId, currentInstance, true);
                // Notifica o WhatsApp para marcar como visto/lido já que a conversa está aberta
                api.post(`/contatos/chats/${currentChat.whatsappId}/seen?instance=${currentInstance}`).catch(() => {});
            }
            fetchChats(true, currentInstance);
        };

        socketInstance.on('connect', () => {
            console.log('✅ ChatPage: Socket conectado! ID:', socketInstance.id);
            setSocketConnected(true);
        });

        socketInstance.on('disconnect', (reason) => {
            console.log('🔴 ChatPage: Socket desconectado! Razão:', reason);
            setSocketConnected(false);
        });

        socketInstance.on('connect_error', (error) => {
            console.error('🚨 ChatPage: Erro de conexão do socket:', error.message);
            setSocketConnected(false);
        });

        socketInstance.on('whatsapp_message', handleIncomingMessage);

        return () => {
            console.log('🔌 Desconectando socket de ChatPage...');
            socketInstance.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fallback de polling silencioso quando o socket estiver offline
    useEffect(() => {
        if (socketConnected) return;

        console.log("⚠️ WebSocket offline. Iniciando fallback de atualização silenciosa (10s)...");
        const interval = setInterval(() => {
            const currentInstance = activeInstanceRef.current;
            const currentChat = activeChatRef.current;
            
            console.log("🔄 Fallback: Sincronizando dados em segundo plano...");
            fetchChats(true, currentInstance);
            if (currentChat) {
                fetchMessages(currentChat.whatsappId, currentInstance, true);
            }
        }, 10000); // 10 segundos

        return () => {
            console.log("🧹 Limpando fallback de atualização silenciosa.");
            clearInterval(interval);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socketConnected]);

    // Quando o usuário troca de aba (Instância A <-> B), recarrega os dados
    useEffect(() => {
        fetchChats();
        setActiveChat(null);
        setMessages([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeInstance]);

    // Handle selecting a chat
    const selectChat = (chat) => {
        setActiveChat(chat);
        fetchMessages(chat.whatsappId);
        
        // Limpa visualmente o contador no frontend imediatamente
        setChats(prev => prev.map(c => isSameJid(c.whatsappId, chat.whatsappId) ? { ...c, unreadCount: 0 } : c));
        
        // Notifica o WhatsApp para marcar como visto/lido
        api.post(`/contatos/chats/${chat.whatsappId}/seen?instance=${activeInstance}`).catch(err => {
            console.error("Erro ao marcar chat como lido:", err);
        });
    };

    // Filtros de busca em tempo real
    const filteredChats = chats
        .filter(c => !c.whatsappId.endsWith('@g.us')) // Apenas privados
        .filter(c => {
            const query = searchQuery.toLowerCase();
            return c.name?.toLowerCase().includes(query) || c.whatsappId?.includes(query);
        });

    // Retorna o nome do grupo a partir do whatsappId do grupo
    const getGroupName = (grupoId) => {
        if (!grupoId) return '';
        const found = groups.find(g => g.whatsappId === grupoId);
        return found ? found.nome : '';
    };

    const filteredContacts = contacts
        .filter(c => !c.whatsappId.endsWith('@g.us')) // Apenas contatos privados (com ou sem grupoId)
        .filter(c => {
            const query = searchQuery.toLowerCase();
            return c.nome?.toLowerCase().includes(query) || c.whatsappId?.includes(query);
        });

    return (
        <div style={containerStyle}>
            <div style={headerStyle}>
                <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '800', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <MessageSquare size={24} color="#25d366" /> Monitoramento em Tempo Real e Chat Humano
                </h1>

                <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                    <div style={{ display: 'flex', background: '#e2e8f0', borderRadius: '8px', padding: '4px' }}>
                        <button
                            onClick={() => setActiveInstance('A')}
                            style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', background: activeInstance === 'A' ? '#fff' : 'transparent', color: activeInstance === 'A' ? '#1e293b' : '#64748b', boxShadow: activeInstance === 'A' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', transition: '0.2s' }}
                        >
                            🟢 A: Marketing
                        </button>
                        <button
                            onClick={() => setActiveInstance('B')}
                            style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', background: activeInstance === 'B' ? '#fff' : 'transparent', color: activeInstance === 'B' ? '#1e293b' : '#64748b', boxShadow: activeInstance === 'B' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', transition: '0.2s' }}
                        >
                            🔵 B: Suporte / Senhas
                        </button>
                    </div>

                    <button onClick={() => { fetchChats(false, activeInstance); fetchContacts(); }} style={refreshBtnStyle}>
                        <RefreshCw size={18} /> Sincronizar Listas
                    </button>
                </div>
            </div>

            <div className="chat-grid" style={gridStyle}>
                {/* LISTA LATERAL (SIDEBAR DE CHATS E CONTATOS) */}
                <div className={`chat-sidebar ${activeChat ? 'chat-sidebar-mobile-hidden' : ''}`} style={sidebarStyle}>

                    {/* Header customizado da sidebar */}
                    <div style={sidebarHeaderStyle}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <span style={{ fontSize: '15px', fontWeight: '800', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                {sidebarTab === 'chats' ? <MessageSquare size={16} color="#25d366" /> : <Users size={16} color="#3b82f6" />}
                                {sidebarTab === 'chats' ? 'Conversas' : 'Lista de Contatos'}
                            </span>
                            <div style={{ display: 'flex', gap: '6px' }}>
                                <button
                                    onClick={() => setSidebarTab(sidebarTab === 'chats' ? 'contacts' : 'chats')}
                                    style={{
                                        ...iconBtnStyle,
                                        backgroundColor: sidebarTab === 'contacts' ? '#cbd5e1' : '#fff',
                                        color: '#334155'
                                    }}
                                    title={sidebarTab === 'chats' ? "Ver Lista de Contatos" : "Ver Conversas Recentes"}
                                >
                                    <Users size={15} />
                                </button>
                                <button
                                    onClick={() => setShowAddModal(true)}
                                    style={iconBtnStyle}
                                    title="Adicionar Novo Contato"
                                >
                                    <UserPlus size={15} />
                                </button>
                            </div>
                        </div>

                        {/* Campo de pesquisa */}
                        <div style={searchWrapperStyle}>
                            <Search size={14} color="#94a3b8" />
                            <input
                                type="text"
                                placeholder={sidebarTab === 'chats' ? "Pesquisar conversas..." : "Pesquisar contatos..."}
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                style={searchInputStyle}
                            />
                        </div>
                    </div>

                    {/* CONTEÚDO DA LISTA LATERAL */}
                    {sidebarTab === 'chats' ? (
                        /* ABA DE CONVERSAS */
                        loadingChats ? (
                            <div style={loadingContainerStyle}><RefreshCw size={20} className="animate-spin" /></div>
                        ) : filteredChats.length === 0 ? (
                            <div style={noDataStyle}>Nenhuma conversa ativa encontrada.</div>
                        ) : (
                            <div style={chatListStyle}>
                                {filteredChats.map(chat => (
                                    <div
                                        key={chat.whatsappId}
                                        onClick={() => selectChat(chat)}
                                        style={{
                                            ...chatCardStyle,
                                            borderLeft: activeChat && isSameJid(activeChat.whatsappId, chat.whatsappId) ? '4px solid #25d366' : '4px solid transparent',
                                            backgroundColor: activeChat && isSameJid(activeChat.whatsappId, chat.whatsappId) ? '#f1f5f9' : '#fff'
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <span style={chatNameStyle}>{chat.name}</span>
                                            {chat.aiEnabled ? (
                                                <span style={badgeAIStyle}><Bot size={11} /> IA Ativa</span>
                                            ) : (
                                                <span style={badgeHumanStyle}><User size={11} /> Humano</span>
                                            )}
                                        </div>
                                        <div style={chatIdStyle}>{chat.whatsappId.split('@')[0]}</div>
                                        {chat.unreadCount > 0 && (
                                            <div style={unreadBadgeStyle}>{chat.unreadCount}</div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )
                    ) : (
                        /* ABA DE CONTATOS */
                        loadingContacts ? (
                            <div style={loadingContainerStyle}><RefreshCw size={20} className="animate-spin" /></div>
                        ) : filteredContacts.length === 0 ? (
                            <div style={noDataStyle}>Nenhum contato encontrado.</div>
                        ) : (
                            <div style={chatListStyle}>
                                {filteredContacts.map(contact => (
                                    <div
                                        key={contact.id}
                                        onClick={() => handleSelectContactToChat(contact)}
                                        style={{
                                            ...chatCardStyle,
                                            borderLeft: activeChat && isSameJid(activeChat.whatsappId, contact.whatsappId) ? '4px solid #3b82f6' : '4px solid transparent',
                                            backgroundColor: activeChat && isSameJid(activeChat.whatsappId, contact.whatsappId) ? '#f1f5f9' : '#fff',
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}
                                    >
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                            <span style={{ ...chatNameStyle, maxWidth: '200px' }}>{contact.nome || 'Sem Nome'}</span>
                                            <div style={chatIdStyle}>{contact.whatsappId.split('@')[0]}</div>
                                            {contact.grupoId && (
                                                <div style={{ fontSize: '10px', color: '#3b82f6', fontWeight: 'bold', marginTop: '2px', fontStyle: 'italic' }}>
                                                    Grupo: {getGroupName(contact.grupoId) || 'Carregando...'}
                                                </div>
                                            )}
                                        </div>
                                        <span style={{ fontSize: '11px', color: '#3b82f6', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            Iniciar →
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )
                    )}
                </div>

                {/* AREA DE CHAT */}
                <div className={`chat-area ${!activeChat ? 'chat-area-mobile-hidden' : ''}`} style={chatAreaStyle}>
                    {activeChat ? (
                        <>
                            <div className="chat-header-resp" style={chatHeaderStyle}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <button
                                        type="button"
                                        onClick={() => setActiveChat(null)}
                                        className="mobile-back-btn"
                                        style={mobileBackBtnStyle}
                                    >
                                        ← Voltar
                                    </button>
                                    <div>
                                        <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#1e293b' }}>{activeChat.name}</div>
                                        <div style={{ fontSize: '12px', color: '#64748b' }}>{activeChat.whatsappId}</div>
                                    </div>
                                </div>
                                <div className="chat-header-actions">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontSize: '13px', fontWeight: 'bold', color: activeChat.aiEnabled ? '#25d366' : '#ee4d2d' }}>
                                            {activeChat.aiEnabled ? 'Robô Respondendo (IA)' : 'Atendimento Humano'}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => toggleAI(activeChat.whatsappId, activeChat.aiEnabled)}
                                            style={{
                                                width: '50px',
                                                height: '26px',
                                                borderRadius: '13px',
                                                backgroundColor: activeChat.aiEnabled ? '#25d366' : '#ee4d2d',
                                                border: 'none',
                                                cursor: 'pointer',
                                                position: 'relative',
                                                transition: '0.3s'
                                            }}
                                        >
                                            <div style={{
                                                width: '18px',
                                                height: '18px',
                                                backgroundColor: '#fff',
                                                borderRadius: '50%',
                                                position: 'absolute',
                                                top: '4px',
                                                left: activeChat.aiEnabled ? '28px' : '4px',
                                                transition: '0.3s',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                            }} />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* MENSAGENS */}
                            <div style={messagesBoxStyle}>
                                {loadingMessages ? (
                                    <div style={loadingContainerStyle}><RefreshCw size={24} className="animate-spin" /></div>
                                ) : messages.length === 0 ? (
                                    <div style={noDataStyle}>Nenhuma mensagem nesta conversa.</div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {messages.map((m, idx) => {
                                            const isMe = m.fromMe;
                                            return (
                                                <div
                                                    key={m.id || idx}
                                                    style={{
                                                        ...messageWrapperStyle,
                                                        alignSelf: isMe ? 'flex-end' : 'flex-start'
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            ...messageBubbleStyle,
                                                            backgroundColor: isMe ? '#25d366' : '#fff',
                                                            color: isMe ? '#fff' : '#1e293b',
                                                            borderBottomRightRadius: isMe ? '0px' : '12px',
                                                            borderBottomLeftRadius: isMe ? '12px' : '0px',
                                                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                                            border: isMe ? 'none' : '1px solid #e2e8f0'
                                                        }}
                                                    >
                                                        {m.hasMedia && (
                                                            <MediaContent
                                                                chatId={activeChat.whatsappId}
                                                                messageId={m.id}
                                                                type={m.type}
                                                                isMe={isMe}
                                                            />
                                                        )}
                                                        <div style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{m.body}</div>
                                                        <div
                                                            style={{
                                                                fontSize: '9px',
                                                                textAlign: 'right',
                                                                marginTop: '4px',
                                                                color: isMe ? 'rgba(255,255,255,0.7)' : '#94a3b8'
                                                            }}
                                                        >
                                                            {new Date(m.timestamp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        <div ref={messagesEndRef} />
                                    </div>
                                )}
                            </div>

                            {/* PREVIEW DO ARQUIVO ANEXADO */}
                            {selectedFile && (
                                <div style={{
                                    padding: '8px 20px',
                                    background: '#f8fafc',
                                    borderTop: '1px solid #e2e8f0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    borderLeft: '4px solid #25d366'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#475569', fontWeight: 'bold' }}>
                                        <Paperclip size={16} style={{ color: '#25d366' }} />
                                        <span style={{ maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedFile.name}</span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setSelectedFile(null)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#ef4444',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            fontSize: '12px',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        <X size={14} /> Remover arquivo
                                    </button>
                                </div>
                            )}

                            {/* ENTRADA DE MENSAGENS */}
                            <form onSubmit={handleSendMessage} style={inputAreaStyle}>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    style={{ display: 'none' }}
                                />
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0 12px',
                                        backgroundColor: '#f1f5f9',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        color: '#475569',
                                        transition: '0.2s'
                                    }}
                                    title="Anexar arquivo"
                                >
                                    <Paperclip size={20} />
                                </button>
                                <input
                                    type="text"
                                    placeholder={activeChat.aiEnabled ? "IA Ativa (Robô). Clique no interruptor acima para desativar e fazer atendimento humano..." : "Atendimento Humano. Digite sua mensagem..."}
                                    value={newMessage}
                                    onChange={e => setNewMessage(e.target.value)}
                                    style={textInputStyle}
                                />
                                <button type="submit" className="chat-send-btn" disabled={sending || (!newMessage.trim() && !selectedFile)} style={sendBtnStyle}>
                                    <Send size={18} /> <span>Enviar</span>
                                </button>
                            </form>
                        </>
                    ) : (
                        <div style={emptyStateStyle}>
                            <Bot size={48} color="#94a3b8" />
                            <p style={{ margin: '15px 0 0 0', fontWeight: '600', color: '#64748b' }}>Nenhuma conversa selecionada</p>
                            <p style={{ margin: '5px 0 0 0', fontSize: '13px', color: '#94a3b8' }}>Selecione um contato ou uma conversa na lista ao lado para monitorar e interagir.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* MODAL DE ADICIONAR NOVO CONTATO */}
            {showAddModal && (
                <div style={modalOverlayStyle}>
                    <div style={modalContentStyle}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0, color: '#1e293b', fontWeight: '800', fontSize: '18px' }}>Adicionar Novo Contato</h3>
                            <button onClick={() => setShowAddModal(false)} style={modalCloseBtnStyle}><X size={18} /></button>
                        </div>
                        <form onSubmit={handleAddContact} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <div>
                                <label style={labelStyle}>Nome do Contato</label>
                                <input
                                    type="text"
                                    placeholder="Ex: João Silva"
                                    value={newContactName}
                                    onChange={e => setNewContactName(e.target.value)}
                                    style={inputStyle}
                                    required
                                />
                            </div>
                            <div>
                                <label style={labelStyle}>Número do WhatsApp</label>
                                <input
                                    type="text"
                                    placeholder="Ex: 5511999999999"
                                    value={newContactPhone}
                                    onChange={e => setNewContactPhone(e.target.value)}
                                    style={inputStyle}
                                    required
                                />
                                <span style={{ fontSize: '11px', color: '#64748b', marginTop: '5px', display: 'block' }}>
                                    Insira com código do país (Brasil: 55) e o DDD. Apenas números.
                                </span>
                            </div>
                            <button type="submit" disabled={savingContact} style={modalSaveBtnStyle}>
                                {savingContact ? 'Salvando contato...' : 'Salvar e Iniciar Conversa'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
            <style>{`.animate-spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

// ESTILOS Customizados e Premium
const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 120px)',
    gap: '20px'
};

const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
};

const refreshBtnStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 15px',
    backgroundColor: '#fff',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    fontWeight: 'bold',
    color: '#475569',
    cursor: 'pointer',
    transition: '0.2s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
};

const gridStyle = {
    display: 'grid',
    gridTemplateColumns: '340px 1fr',
    flex: 1,
    backgroundColor: '#fff',
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)'
};

const sidebarStyle = {
    borderRight: '1px solid #e2e8f0',
    display: 'flex',
    flexDirection: 'column',
    background: '#f8fafc',
    overflowY: 'hidden'
};

const sidebarHeaderStyle = {
    padding: '15px 20px',
    backgroundColor: '#fff',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    flexDirection: 'column'
};

const iconBtnStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    backgroundColor: '#fff',
    color: '#64748b',
    cursor: 'pointer',
    transition: '0.2s'
};

const searchWrapperStyle = {
    display: 'flex',
    alignItems: 'center',
    background: '#f1f5f9',
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    gap: '8px'
};

const searchInputStyle = {
    border: 'none',
    background: 'none',
    outline: 'none',
    fontSize: '13px',
    color: '#334155',
    width: '100%'
};

const chatListStyle = {
    display: 'flex',
    flexDirection: 'column',
    padding: '10px',
    gap: '8px',
    overflowY: 'auto',
    flex: 1
};

const chatCardStyle = {
    padding: '12px 15px',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: '0.2s',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    position: 'relative',
    boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
};

const chatNameStyle = {
    fontWeight: 'bold',
    fontSize: '13px',
    color: '#1e293b',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '180px'
};

const chatIdStyle = {
    fontSize: '11px',
    color: '#64748b'
};

const badgeAIStyle = {
    fontSize: '10px',
    padding: '3px 8px',
    borderRadius: '20px',
    backgroundColor: '#dcfce7',
    color: '#15803d',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
};

const badgeHumanStyle = {
    fontSize: '10px',
    padding: '3px 8px',
    borderRadius: '20px',
    backgroundColor: '#fee2e2',
    color: '#b91c1c',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
};

const unreadBadgeStyle = {
    position: 'absolute',
    right: '15px',
    bottom: '12px',
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    backgroundColor: '#ee4d2d',
    color: '#fff',
    fontSize: '10px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
};

const chatAreaStyle = {
    display: 'flex',
    flexDirection: 'column',
    background: '#f1f5f9',
    overflow: 'hidden'
};

const chatHeaderStyle = {
    padding: '15px 20px',
    background: '#fff',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
};

const mobileBackBtnStyle = {
    display: 'none' // Será exibido no CSS responsivo do mobile
};

const messagesBoxStyle = {
    flex: 1,
    padding: '20px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
};

const messageWrapperStyle = {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '70%'
};

const messageBubbleStyle = {
    padding: '10px 14px',
    borderRadius: '12px',
    fontSize: '14px',
    lineHeight: '1.4'
};

const inputAreaStyle = {
    padding: '15px 20px',
    background: '#fff',
    borderTop: '1px solid #e2e8f0',
    display: 'flex',
    gap: '10px'
};

const textInputStyle = {
    flex: 1,
    padding: '12px 15px',
    borderRadius: '8px',
    border: '1px solid #cbd5e1',
    fontSize: '14px',
    outline: 'none'
};

const sendBtnStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '0 20px',
    backgroundColor: '#25d366',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: '0.2s'
};

const loadingContainerStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    padding: '40px'
};

const noDataStyle = {
    textAlign: 'center',
    color: '#94a3b8',
    padding: '30px 20px',
    fontSize: '13px'
};

const emptyStateStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    textAlign: 'center',
    padding: '40px'
};

// ESTILOS DO MODAL
const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2000
};

const modalContentStyle = {
    backgroundColor: '#fff',
    padding: '30px',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '450px',
    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
    border: '1px solid #e2e8f0'
};

const modalCloseBtnStyle = {
    background: 'none',
    border: 'none',
    color: '#64748b',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    padding: '5px'
};

const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: 'bold',
    color: '#334155',
    marginBottom: '6px'
};

const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #cbd5e1',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box'
};

const modalSaveBtnStyle = {
    width: '100%',
    padding: '12px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: '0.2s',
    marginTop: '10px'
};

export default ChatPage;
